import type { Fundamentals } from "@/lib/data/types";
import { SCORE_CONFIG } from "@/lib/config";

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export interface FinancialHealthPart {
  key: "debtToEquity" | "netDebtToEbitda" | "cashCushion" | "netMargin";
  label: string;
  /** 0-100, wie beim Gesamtscore. */
  score: number;
  /** Anteil am Gesamtscore (0-1), je nach Datenlage anteilig renormalisiert. */
  weight: number;
  /** Der ungerundete, unnormierte Wert HINTER dem 0-100-Score (z.B. echte D/E-Ratio
   * oder Nettomarge als Bruchzahl) - fuer die Anzeige/Tooltips (Feedback-Runde 19
   * Punkt 14), die bisher nur den abstrakten Teil-Score ohne konkrete Zahl zeigten. */
  rawValue: number;
}

export interface FinancialHealth {
  score: number; // 0-100
  label: string;
  /** Die 1-3 Teil-Scores, aus denen sich `score` zusammensetzt - macht sichtbar, WARUM. */
  parts: FinancialHealthPart[];
  /** Geschaetzte liquide Mittel (Gesamtverschuldung - Nettoverschuldung), falls beide vorliegen. */
  estimatedCash?: number;
  /** Erklaerender Hinweis, WARUM ein Teil-Score fehlt, statt ihn einfach kommentarlos
   * verschwinden zu lassen (CLAUDE.md: "Confidence/DataQuality anzeigen"). Aktuell nur
   * fuer den bekannten Fall befuellt, dass debtToEquity wegen negativen bilanziellen
   * Eigenkapitals fehlt (z.B. MCD, Feedback-Runde 18 Punkt 9) - andere Luecken bleiben
   * weiterhin ohne Erklaerung (Ursache nicht sicher genug identifizierbar). */
  missingPartsNote?: string;
}

const HEALTH_LABELS: [threshold: number, label: string][] = [
  [80, "Sehr gesund"],
  [60, "Gesund"],
  [40, "Durchschnittlich"],
  [20, "Angespannt"],
  [0, "Kritisch"],
];

function labelFor(score: number): string {
  return HEALTH_LABELS.find(([threshold]) => score >= threshold)?.[1] ?? "Kritisch";
}

const PART_STRENGTH_LABELS: [threshold: number, label: string][] = [
  [70, "Stark"],
  [40, "Mittel"],
  [0, "Schwach"],
];

/**
 * Kurz-Einordnung eines einzelnen Teil-Scores (Runde 8 Punkt 15 / Runde 4 Punkt 9a:
 * "mehr visuelle Unterstuetzung, WARUM der Score so ausfaellt") - beantwortet direkt an
 * jedem Ring, ob DIESER Teilwert fuer sich stark/mittel/schwach ist, statt nur eine Zahl
 * ohne Einordnung zu zeigen. Eigene, absichtlich groebere 3-Stufen-Skala statt der
 * 5-stufigen HEALTH_LABELS oben - ein einzelner Teil-Score braucht keine so feine
 * Abstufung wie der Gesamtscore. Reine Funktion, kein I/O.
 */
export function partStrengthLabel(score: number): string {
  return PART_STRENGTH_LABELS.find(([threshold]) => score >= threshold)?.[1] ?? "Schwach";
}

/**
 * Heuristischer 0-100-Gesamteindruck der finanziellen Gesundheit fuer die Gauge-Anzeige
 * (bewusst getrennt vom deterministischen Erikonomie-Score in lib/scoring). Kombiniert
 * Verschuldungsgrad, Net Debt/EBITDA, Netto-/Bruttoschulden-Verhaeltnis (Liquiditaetspuffer)
 * und Nettomarge (Ertragskraft zur Schuldenbedienung) zu je 25 %, je nach Datenlage anteilig
 * gewichtet. Verschuldungsgrad hatte bis 2026-08-03 als alleiniges Verschuldungssignal 50 %
 * Gewicht - Erik (Beispiel Johnson & Johnson: AAA-Rating, aber nur 64/100) hat zurecht
 * hinterfragt, dass eine rein bilanzielle D/E-Ratio (leicht verzerrbar durch Aktienrueckkaeufe
 * oder Abspaltungen wie Kenvue) allein ueber die Haelfte des Scores bestimmt, waehrend der
 * normale Erikonomie-Score fuer dieselbe Aktie ueber Net Debt/EBITDA laengst eine exzellente
 * Verschuldung attestiert. Net Debt/EBITDA jetzt als vierte, gleichgewichtete Komponente
 * ergaenzt (dieselbe Formel/Schwelle wie scoreNetDebtToEbitda in lib/scoring/scoring.ts,
 * SCORE_CONFIG.thresholds.netDebtToEbitda) statt Verschuldungsgrad zu ersetzen - beide
 * Kennzahlen bleiben als sich ergaenzende, aber gleichrangige Signale sichtbar.
 */
export function computeFinancialHealth(f: Fundamentals): FinancialHealth | undefined {
  const rawParts: FinancialHealthPart[] = [];

  if (f.debtToEquity !== undefined) {
    // D/E 0 -> 100, D/E 1.0 -> 0 (linear, danach gekappt).
    rawParts.push({
      key: "debtToEquity",
      label: "Verschuldungsgrad",
      score: clamp(100 - f.debtToEquity * 100, 0, 100),
      weight: 0.25,
      rawValue: f.debtToEquity,
    });
  }

  if (f.netDebt !== undefined && f.ebitda !== undefined && f.ebitda > 0) {
    // Gleiche Schwellen wie im Erikonomie-Score (scoreNetDebtToEbitda), hier auf die
    // 0-100- statt 0-10-Skala uebertragen: ratio 0 (schuldenfrei) -> 100, ratio 4.0x -> 0.
    // Nettocash (ratio < 0) landet automatisch ueber 100 und wird auf 100 gekappt - kein
    // eigener Sonderfall noetig.
    const { badRatio, goodRatio } = SCORE_CONFIG.thresholds.netDebtToEbitda;
    const ratio = f.netDebt / f.ebitda;
    rawParts.push({
      key: "netDebtToEbitda",
      label: "Net Debt / EBITDA",
      score: clamp((100 * (ratio - badRatio)) / (goodRatio - badRatio), 0, 100),
      weight: 0.25,
      rawValue: ratio,
    });
  }

  if (f.totalDebt !== undefined && f.netDebt !== undefined && f.totalDebt > 0) {
    // Wie viel der Schulden ist durch Cash gedeckt? cushion=1 -> schuldenfrei/Netto-Cash,
    // cushion=0 -> keine Deckung.
    const cushion = clamp(1 - f.netDebt / f.totalDebt, -1, 1);
    rawParts.push({
      key: "cashCushion",
      label: "Liquiditätspuffer",
      score: clamp(50 + cushion * 50, 0, 100),
      weight: 0.25,
      rawValue: cushion,
    });
  }

  if (f.netMargin !== undefined) {
    // Nettomarge 0% -> 30 Punkte Basis, 20%+ -> 100 Punkte.
    rawParts.push({
      key: "netMargin",
      label: "Ertragskraft",
      score: clamp(30 + (f.netMargin / 0.2) * 70, 0, 100),
      weight: 0.25,
      rawValue: f.netMargin,
    });
  }

  if (rawParts.length === 0) return undefined;
  const totalWeight = rawParts.reduce((sum, p) => sum + p.weight, 0);
  const score = rawParts.reduce((sum, p) => sum + p.score * p.weight, 0) / totalWeight;
  // Gewichte auf die tatsaechlich verfuegbaren Teil-Scores renormalisiert (summieren zu 1),
  // damit die Balken in der UI die reale Zusammensetzung zeigen statt der Rohgewichte.
  const parts = rawParts.map((p) => ({ ...p, weight: p.weight / totalWeight }));
  const estimatedCash =
    f.totalDebt !== undefined && f.netDebt !== undefined && f.totalDebt >= f.netDebt
      ? f.totalDebt - f.netDebt
      : undefined;
  const missingPartsNote =
    f.debtToEquity === undefined && f.bookValuePerShare !== undefined && f.bookValuePerShare < 0
      ? "Verschuldungsgrad hier nicht ausweisbar: negatives bilanzielles Eigenkapital (typischerweise durch hohe Aktienrückkäufe) macht die Kennzahl nicht aussagekräftig berechenbar. Der Score basiert auf den übrigen Teilkennzahlen."
      : undefined;

  return { score: Math.round(score), label: labelFor(score), parts, estimatedCash, missingPartsNote };
}

/**
 * Kurzer, faktischer Erklaertext zum Gesundheits-Score (backlog.md -> Feedback-Runde
 * 2026-07-14, "was der Gesamtscore oder die Nebenscores bedeuten") - deterministisch aus
 * den bereits berechneten Werten abgeleitet, KEIN LLM-Aufruf (dieselbe Grenze wie beim
 * bestehenden KI-Copilot "hinter Flag", hier bewusst umgangen statt verletzt). Nennt den
 * staerksten und schwaechsten Teilwert konkret beim Namen statt einer generischen Phrase
 * - gleiches Prinzip wie `buildSummary()` fuer den Erikonomie-Score
 * (lib/scoring/scoring.ts). Rein deskriptiv, keine Handlungsempfehlung. Reine Funktion,
 * kein I/O.
 */
export function buildFinancialHealthSummary(health: FinancialHealth): string {
  const base = `Gesamtscore ${health.score}/100 (${health.label}).`;
  if (health.parts.length === 0) return base;

  const sorted = [...health.parts].sort((a, b) => b.score - a.score);
  const strongest = sorted[0];
  if (sorted.length === 1) {
    return `${base} Einzige verfügbare Teilkennzahl: ${strongest.label} (${Math.round(strongest.score)}/100, Gewicht ${Math.round(strongest.weight * 100)} %).`;
  }

  const weakest = sorted[sorted.length - 1];
  return `${base} Stärkster Teilwert: ${strongest.label} (${Math.round(strongest.score)}/100). Schwächster Teilwert: ${weakest.label} (${Math.round(weakest.score)}/100).`;
}
